/**
 * Hash Generator Service
 * Implements business logic for generating cryptographic hashes and checksums
 */

import CryptoJS from 'crypto-js';
import { EncodingOperation, EncoderToolType } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export type HashAlgorithm = 'md5' | 'sha1' | 'sha256' | 'sha512' | 'crc32';

export class HashGeneratorService {
  /**
   * Generate hash from text
   */
  static async generate(
    text: string,
    algorithm: HashAlgorithm = 'sha256'
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(text,
        CommonValidator.getValidationRules(EncoderToolType.HASH_GENERATOR)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.HASH_GENERATOR, validation.suggestion);
      }

      // Generate hash
      const hash = this.generateHash(text, algorithm);

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.HASH_GENERATOR,
        operationType: 'encode',
        inputData: text,
        outputData: hash,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          algorithm,
          hashLength: hash.length,
          inputLength: text.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to generate hash',
        EncoderToolType.HASH_GENERATOR,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Generate hash using specified algorithm
   */
  private static generateHash(text: string, algorithm: HashAlgorithm): string {
    switch (algorithm) {
      case 'md5':
        return CryptoJS.MD5(text).toString();
      case 'sha1':
        return CryptoJS.SHA1(text).toString();
      case 'sha256':
        return CryptoJS.SHA256(text).toString();
      case 'sha512':
        return CryptoJS.SHA512(text).toString();
      case 'crc32':
        // Simple CRC32 implementation
        return this.crc32(text).toString(16).padStart(8, '0');
      default:
        return CryptoJS.SHA256(text).toString();
    }
  }

  /**
   * Simple CRC32 implementation
   */
  private static crc32(str: string): number {
    const makeCRCTable = () => {
      let c;
      const crcTable = [];
      for (let n = 0; n < 256; n++) {
        c = n;
        for (let k = 0; k < 8; k++) {
          c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
        }
        crcTable[n] = c;
      }
      return crcTable;
    };

    const crcTable = makeCRCTable();
    let crc = 0 ^ (-1);

    for (let i = 0; i < str.length; i++) {
      crc = (crc >>> 8) ^ crcTable[(crc ^ str.charCodeAt(i)) & 0xFF];
    }

    return (crc ^ (-1)) >>> 0;
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `hash-generator-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
